<template>
	<div
		v-if="hasDetails"
		class="c-enrichment-details"
		role="group"
		aria-label="Ad details"
	>
		<div v-if="brandCreatorName" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Brand/Creator</span>
			<img
				v-if="avatarUrl && failedAvatarUrl !== avatarUrl"
				class="c-enrichment-details__avatar"
				:src="avatarUrl"
				alt=""
				@error="onAvatarError"
			/>
			<svg
				v-else
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M4 20V19C4 16.7909 5.79086 15 8 15H16C18.2091 15 20 16.7909 20 19V20"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
				/>
				<path
					d="M16.5 7.5C16.5 9.98528 14.4853 12 12 12C9.51472 12 7.5 9.98528 7.5 7.5C7.5 5.01472 9.51472 3 12 3C14.4853 3 16.5 5.01472 16.5 7.5Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
				/>
			</svg>
			<span class="c-enrichment-details__value">{{ brandCreatorName }}</span>
		</div>

		<div class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Saved by</span>
			<img
				v-if="savedByAvatarUrl && failedSavedByAvatarUrl !== savedByAvatarUrl"
				class="c-enrichment-details__avatar"
				:src="savedByAvatarUrl"
				alt=""
				@error="onSavedByAvatarError"
			/>
			<svg
				v-else
				class="c-enrichment-details__saved-by-icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<circle cx="12" cy="12" r="11" />
				<circle cx="12" cy="9" r="3" fill="var(--color-bg-1)" />
				<path
					d="M5.5 19.3C6.1 16.5 8.7 14.5 12 14.5S17.9 16.5 18.5 19.3"
					fill="var(--color-bg-1)"
				/>
			</svg>
			<span
				class="c-enrichment-details__value c-enrichment-details__saved-by-value"
			>
				<span class="c-enrichment-details__saved-by-name">{{
					savedByName
				}}</span>
				<span
					v-if="formattedSavedOn"
					class="c-enrichment-details__saved-by-date"
					>{{ formattedSavedOn }}</span
				>
			</span>
		</div>

		<div v-if="statusText" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Status</span>
			<svg
				:class="[
					'c-enrichment-details__status-dot',
					{
						active: block.status === 'ACTIVE',
						pending: block.status === 'PENDING',
					},
				]"
				width="6"
				height="6"
				viewBox="0 0 6 6"
				fill="currentColor"
				role="img"
				:aria-label="statusLabel"
				:title="statusLabel"
			>
				<circle cx="3" cy="3" r="3" />
			</svg>
			<span class="c-enrichment-details__value">{{ statusText }}</span>
		</div>

		<div v-if="timeRunning" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Time running</span>
			<svg
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M18 8L22 12M22 12L18 16M22 12H2M6 8L2 12M2 12L6 16"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			<span class="c-enrichment-details__value">{{ timeRunning }}</span>
		</div>

		<div v-if="ctaText" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">CTA text</span>
			<svg
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M11 3V4.5M16.6569 5.34326L15.5962 6.40392M6.40381 15.5962L5.34315 16.6569M4.5 11H3M6.40381 6.40381L5.34315 5.34315M14.1365 20.8008L10.4008 11.0466C10.2461 10.6426 10.6426 10.2461 11.0466 10.4008L20.8008 14.1365C21.1883 14.2849 21.2368 14.8136 20.8827 15.03L17.353 17.1871C17.2853 17.2284 17.2284 17.2853 17.1871 17.353L15.03 20.8827C14.8136 21.2368 14.2849 21.1883 14.1365 20.8008Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			<span class="c-enrichment-details__value">{{ ctaText }}</span>
		</div>

		<div v-if="formatLabel" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Format</span>
			<svg
				v-if="formatIcon === 'video'"
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M3 7C3 5.89543 3.89543 5 5 5H13C14.1046 5 15 5.89543 15 7V17C15 18.1046 14.1046 19 13 19H5C3.89543 19 3 18.1046 3 17V7Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linejoin="round"
				/>
				<path
					d="M15.5528 9.72361L19.5528 7.72361C20.2177 7.39116 21 7.87465 21 8.61803V15.382C21 16.1253 20.2177 16.6088 19.5528 16.2764L15.5528 14.2764C15.214 14.107 15 13.7607 15 13.382V10.618C15 10.2393 15.214 9.893 15.5528 9.72361Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linejoin="round"
				/>
			</svg>
			<svg
				v-else-if="formatIcon === 'carousel'"
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M15 4H9C7.89543 4 7 4.89543 7 6V18C7 19.1046 7.89543 20 9 20H15C16.1046 20 17 19.1046 17 18V6C17 4.89543 16.1046 4 15 4Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M17 6H19C20.1046 6 21 6.89543 21 8V16C21 17.1046 20.1046 18 19 18H17"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M7 18H5C3.89543 18 3 17.1046 3 16V8C3 6.89543 3.89543 6 5 6H7"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			<svg
				v-else
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M4 6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
				/>
				<path
					d="M16 20.0002L9.41421 13.4144C8.63316 12.6334 7.36684 12.6334 6.58579 13.4144L4 16.0002"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
				/>
				<path
					d="M14.5 11.5C15.6046 11.5 16.5 10.6046 16.5 9.5C16.5 8.39543 15.6046 7.5 14.5 7.5C13.3954 7.5 12.5 8.39543 12.5 9.5C12.5 10.6046 13.3954 11.5 14.5 11.5Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
				/>
			</svg>
			<span class="c-enrichment-details__value">{{ formatLabel }}</span>
		</div>

		<div v-if="niche" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Meta Niche</span>
			<svg
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M21 12.0002C21 16.9708 16.9706 21.0002 12 21.0002C7.02944 21.0002 3 16.9708 3 12.0002C3 7.02963 7.02944 3.00019 12 3.00019M16.7712 13.4996C16.1341 15.5283 14.2389 16.9998 12 16.9998C9.23858 16.9998 7 14.7612 7 11.9998C7 9.76099 8.47145 7.86584 10.5 7.22871M14 10.0002H17.1716C17.702 10.0002 18.2107 9.78948 18.5858 9.41441L21.214 6.78622C21.3454 6.65481 21.2926 6.43105 21.1162 6.37228L18.5 5.50019L17.6279 2.88394C17.5691 2.70763 17.3454 2.65481 17.214 2.78622L14.5858 5.41441C14.2107 5.78948 14 6.29819 14 6.82862V10.0002ZM14 10.0002L12 12.0002"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			<span class="c-enrichment-details__value">{{ niche }}</span>
		</div>

		<div v-if="videoDuration" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Video length</span>
			<svg
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M12 12L6.8906 8.59373C6.3342 8.2228 6 7.59834 6 6.92963V3H18V6.92963C18 7.59834 17.6658 8.2228 17.1094 8.59373L12 12ZM12 12L17.1094 15.4063C17.6658 15.7772 18 16.4017 18 17.0704V21H6V17.0704C6 16.4017 6.3342 15.7772 6.8906 15.4063L12 12ZM20 21H4M20 3H4"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			<span class="c-enrichment-details__value">{{ videoDuration }}</span>
		</div>

		<div v-if="contentTypes.length" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Content types</span>
			<svg
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M4 7C4 8.65685 5.34315 10 7 10C8.65685 10 10 8.65685 10 7C10 5.34315 8.65685 4 7 4C5.34315 4 4 5.34315 4 7Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M4 17C4 18.6569 5.34315 20 7 20C8.65685 20 10 18.6569 10 17C10 15.3431 8.65685 14 7 14C5.34315 14 4 15.3431 4 17Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M14 7C14 8.65685 15.3431 10 17 10C18.6569 10 20 8.65685 20 7C20 5.34315 18.6569 4 17 4C15.3431 4 14 5.34315 14 7Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M14 17C14 18.6569 15.3431 20 17 20C18.6569 20 20 18.6569 20 17C20 15.3431 18.6569 14 17 14C15.3431 14 14 15.3431 14 17Z"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			<span class="c-enrichment-details__value">{{
				contentTypes.join(', ')
			}}</span>
		</div>

		<div v-if="hookTypes.length" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Hooks</span>
			<svg
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<circle
					cx="9"
					cy="6"
					r="3"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M9 9V17C9 19.2091 10.7909 21 13 21C15.2091 21 17 19.2091 17 17V12L14 15"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			<span class="c-enrichment-details__value">{{
				hookTypes.join(', ')
			}}</span>
		</div>

		<div v-if="language" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Language</span>
			<span class="c-enrichment-details__emoji" aria-hidden="true">{{
				languageEmoji
			}}</span>
			<span class="c-enrichment-details__value">{{ language }}</span>
		</div>

		<div v-if="adNetworks.length" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Ad networks</span>
			<span class="c-enrichment-details__networks">
				<span
					v-for="network in adNetworks"
					:key="network.slug"
					class="c-enrichment-details__network"
				>
					<img v-if="network.logo" :src="network.logo" alt="" />
					<span>{{ network.title }}</span>
				</span>
			</span>
		</div>

		<div
			v-for="link in externalLinks"
			:key="link.label"
			class="c-enrichment-details__row"
		>
			<span class="c-enrichment-details__label">{{ link.label }}</span>
			<svg
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M10 5.5L10.7514 4.75837C13.0959 2.41387 16.8971 2.41388 19.2416 4.75837C21.5861 7.10287 21.5861 10.9041 19.2416 13.2486L18.5 14M5.5 10L4.75837 10.7514C2.41387 13.0959 2.41388 16.8971 4.75838 19.2416C7.10287 21.5861 10.9041 21.5861 13.2486 19.2416L14 18.5M10 14L14 10"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			<a
				class="c-enrichment-details__link"
				:href="link.url"
				target="_blank"
				rel="noopener noreferrer"
				:title="link.url"
				>{{ link.url }}</a
			>
		</div>

		<div v-if="block.metaId" class="c-enrichment-details__row">
			<span class="c-enrichment-details__label">Ad ID</span>
			<svg
				class="c-enrichment-details__icon"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					d="M12 3V6M12 18V21"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M18.3643 5.63574L16.2429 7.75706"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M16.2427 16.2422L18.364 18.3635"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M18 12H21"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M12 12L3 12"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
				/>
				<path
					d="M5.63634 18.3639L12.0002 12.0002L5.63623 5.63574"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			<span class="c-enrichment-details__value">{{ block.metaId }}</span>
		</div>
	</div>
</template>

<script>
import { format } from 'date-fns';

function formatDate(value) {
	if (!value) return null;
	const date = new Date(value);
	if (isNaN(date.getTime())) return null;
	return format(date, 'MMM d, yyyy');
}

function isWebLink(url) {
	return typeof url === 'string' && /^https?:\/\//i.test(url);
}

function humanizeFormat(value) {
	const names = {
		dco: 'Dynamic Content Optimization',
		dpa: 'Dynamic Product Ads',
		event: 'Event',
		page_like: 'Page Like',
		multi_images: 'Multiple Images',
		multi_medias: 'Multiple Media',
		multi_videos: 'Multiple Videos',
		carousel: 'Carousel',
		video: 'Video',
		image: 'Image',
		text: 'Text',
	};

	if (names[value]) return names[value];
	return value
		.split('_')
		.map(function (word) {
			return word.charAt(0).toUpperCase() + word.slice(1);
		})
		.join(' ');
}

const languageEmojis = {
	en: '🇬🇧',
	es: '🇪🇸',
	fr: '🇫🇷',
	de: '🇩🇪',
	it: '🇮🇹',
	pt: '🇵🇹',
	nl: '🇳🇱',
	sv: '🇸🇪',
	no: '🇳🇴',
	da: '🇩🇰',
	fi: '🇫🇮',
	pl: '🇵🇱',
	cs: '🇨🇿',
	sk: '🇸🇰',
	hu: '🇭🇺',
	ro: '🇷🇴',
	el: '🇬🇷',
	tr: '🇹🇷',
	ru: '🇷🇺',
	uk: '🇺🇦',
	ar: '🇸🇦',
	he: '🇮🇱',
	hi: '🇮🇳',
	bn: '🇧🇩',
	ur: '🇵🇰',
	zh: '🇨🇳',
	ja: '🇯🇵',
	ko: '🇰🇷',
	id: '🇮🇩',
	ms: '🇲🇾',
	vi: '🇻🇳',
	th: '🇹🇭',
	tl: '🇵🇭',
	sw: '🇰🇪',
};

const networkNames = {
	facebook: 'Facebook',
	instagram: 'Instagram',
	'instagram-feed': 'Instagram Feed',
	'instagram-reel': 'Instagram Reels',
	messenger: 'Messenger',
	audience_network: 'Audience Network',
	threads: 'Threads',
	whatsapp: 'WhatsApp',
	tiktok: 'TikTok',
	'tiktok-library': 'TikTok',
	'tiktok-top-ads': 'TikTok',
	linkedin: 'LinkedIn',
	google: 'Google',
	pinterest: 'Pinterest',
	youtube: 'YouTube',
	'upload-custom': 'Manual upload',
};

const networkLogos = {
	facebook: '/brands/facebook.svg',
	instagram: '/brands/instagram.svg',
	'instagram-feed': '/brands/instagram.svg',
	'instagram-reel': '/brands/instagram.svg',
	messenger: '/brands/messenger.svg',
	audience_network: '/brands/audience_network.svg',
	threads: '/brands/threads.svg',
	whatsapp: '/brands/whatsapp.svg',
	tiktok: '/brands/tiktok.png',
	'tiktok-library': '/brands/tiktok.png',
	'tiktok-top-ads': '/brands/tiktok.png',
	linkedin: '/brands/linkedin.png',
	google: '/brands/google.svg',
	pinterest: '/brands/pinterest.svg',
	youtube: '/brands/youtube.svg',
	'upload-custom': '/brands/custom-upload.svg',
};

export default {
	data: function () {
		return {
			failedAvatarUrl: null,
			failedSavedByAvatarUrl: null,
		};
	},
	props: {
		block: {
			type: Object,
			required: true,
		},
		savedBy: {
			type: Object,
			default: null,
		},
		savedOn: {
			type: String,
			default: null,
		},
	},
	computed: {
		savedByName: function () {
			return this.savedBy && this.savedBy.name
				? this.savedBy.name
				: 'Unknown user';
		},
		formattedSavedOn: function () {
			return formatDate(this.savedOn);
		},
		savedByAvatarUrl: function () {
			const avatar = this.savedBy && this.savedBy.avatar;
			if (!avatar || !avatar.name) return null;
			if (/^https?:\/\//.test(avatar.name)) return avatar.name;
			return `${this.$store.getters['config/assetUrl']}/${avatar.name}`;
		},
		brandCreatorName: function () {
			const block = this.block;
			const brand = block.brand;
			const meta = block.meta || {};
			const advertiser = meta.advertiser;
			const snapshot = meta.snapshot;
			if (brand && (brand.name || brand.brand_name)) {
				return brand.name || brand.brand_name;
			}
			if (advertiser && advertiser.name) return advertiser.name;
			if (snapshot && (snapshot.currentPageName || snapshot.pageName)) {
				return snapshot.currentPageName || snapshot.pageName;
			}
			if (meta.brandName) return meta.brandName;
			if (block.author && block.author.name) return block.author.name;
			return null;
		},
		avatarUrl: function () {
			const block = this.block;
			const meta = block.meta || {};
			const advertiser = meta.advertiser;
			const snapshot = meta.snapshot;
			let path = null;
			if (block.brand && block.brand.logo) path = block.brand.logo;
			if (!path && advertiser && advertiser.avatar) {
				path = advertiser.avatar.name || advertiser.avatar.assetPath;
			}
			if (!path && snapshot && snapshot.pageProfilePictureUrl) {
				path = snapshot.pageProfilePictureUrl;
			}
			if (!path && block.author && block.author.assetPath) {
				path = block.author.assetPath;
			}
			if (!path) return null;
			if (/^https?:\/\//.test(path)) return path;
			return `${this.$store.getters['config/assetUrl']}/${path}`;
		},
		statusLabel: function () {
			const names = {
				ACTIVE: 'Active',
				INACTIVE: 'Inactive',
				PENDING: 'Pending',
				NA: 'Not available',
				NOT_IN_LIBRARY: 'Not in library',
			};
			return names[this.block.status] || 'Unknown';
		},
		statusText: function () {
			if (this.block.type !== 'facebook') return null;
			const start = formatDate(this.block.startDate);
			const end = formatDate(this.block.endDate);
			if (start && end) return `${start} to ${end}`;
			if (start && this.block.status === 'ACTIVE') return `${start} to Present`;
			if (start) return start;
			return this.statusLabel;
		},
		timeRunning: function () {
			if (
				this.block.type !== 'facebook' &&
				this.block.type !== 'tiktok-library'
			)
				return null;
			const days = Number(this.block.activeDays);
			if (!Number.isFinite(days) || days <= 0) return null;
			return `${days} ${days === 1 ? 'Day' : 'Days'}`;
		},
		ctaText: function () {
			const value = this.block.meta && this.block.meta.cta;
			if (typeof value !== 'string' || !value.trim()) return null;
			return value
				.trim()
				.replace(/[_-]+/g, ' ')
				.split(/\s+/)
				.map(function (word) {
					return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
				})
				.join(' ');
		},
		formatCode: function () {
			const value = this.block.displayFormat || this.block.assetType;
			return typeof value === 'string' ? value.toLowerCase() : null;
		},
		formatLabel: function () {
			return this.formatCode ? humanizeFormat(this.formatCode) : null;
		},
		formatIcon: function () {
			const code = this.formatCode;
			if (
				code === 'dco' ||
				code === 'dpa' ||
				code === 'carousel' ||
				code === 'multi_images' ||
				code === 'multi_medias' ||
				code === 'multi_videos'
			)
				return 'carousel';
			if (this.block.assetType === 'video' || code === 'video') return 'video';
			return 'image';
		},
		niche: function () {
			if (
				this.block.type !== 'facebook' ||
				!Array.isArray(this.block.categories) ||
				!this.block.categories.length
			)
				return null;
			const category = this.block.categories[0];
			if (typeof category === 'string') return category;
			return category && typeof category.text === 'string'
				? category.text
				: null;
		},
		contentTypes: function () {
			return Array.isArray(this.block.contentTypes)
				? this.block.contentTypes
				: [];
		},
		hookTypes: function () {
			return Array.isArray(this.block.hookTypes) ? this.block.hookTypes : [];
		},
		videoDuration: function () {
			if (this.block.assetType !== 'video') return null;
			const duration = Number(this.block.duration);
			if (!Number.isFinite(duration) || duration <= 0) return null;
			const seconds = Math.round(duration);
			const minutes = Math.floor(seconds / 60);
			return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
		},
		language: function () {
			return typeof this.block.lang === 'string'
				? this.block.lang.toUpperCase()
				: null;
		},
		languageEmoji: function () {
			const code =
				typeof this.block.lang === 'string'
					? this.block.lang.toLowerCase()
					: '';
			return languageEmojis[code] || '🌐';
		},
		adNetworks: function () {
			let networks = [];
			if (Array.isArray(this.block.networks)) {
				networks = this.block.networks.filter(function (network) {
					return typeof network === 'string' && network.trim();
				});
			}
			const blockType =
				typeof this.block.type === 'string'
					? this.block.type.toLowerCase()
					: '';
			if (!networks.length && networkNames[blockType]) networks = [blockType];
			return networks.map(function (network) {
				const slug = network.trim().toLowerCase();
				return {
					slug: slug,
					title: networkNames[slug] || humanizeFormat(slug),
					logo: networkLogos[slug] || null,
				};
			});
		},
		landingPage: function () {
			const meta = this.block.meta;
			if (isWebLink(this.block.ctaLink)) return this.block.ctaLink;
			if (meta && isWebLink(meta.ctaLink)) return meta.ctaLink;
			return null;
		},
		externalLinks: function () {
			const block = this.block;
			const links = [];
			const addLink = function (label, url) {
				if (!isWebLink(url)) return;
				for (let i = 0; i < links.length; i++) {
					if (links[i].url === url) return;
				}
				links.push({ label: label, url: url });
			};

			addLink('Landing page', this.landingPage);
			if (block.type === 'facebook') {
				const meta = block.meta || {};
				if (meta.pageId) {
					addLink(
						'Ad library',
						`https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=ALL&view_all_page_id=${encodeURIComponent(
							meta.pageId
						)}&search_type=page&media_type=all`
					);
				} else if (block.metaId) {
					addLink(
						'Ad library',
						`https://www.facebook.com/ads/library/?id=${encodeURIComponent(
							block.metaId
						)}`
					);
				}
				addLink('Facebook brand page', block.source);
			} else if (
				block.type === 'tiktok-library' ||
				block.type === 'tiktok-top-ads'
			) {
				addLink('TikTok ad library', block.source);
			} else {
				addLink('Source', block.source);
			}
			if (block.meta) addLink('CTA link', block.meta.ctaLink);
			return links;
		},
		hasDetails: function () {
			return (
				this.savedByName ||
				this.brandCreatorName ||
				this.statusText ||
				this.timeRunning ||
				this.ctaText ||
				this.formatLabel ||
				this.niche ||
				this.contentTypes.length ||
				this.hookTypes.length ||
				this.videoDuration ||
				this.language ||
				this.adNetworks.length ||
				this.externalLinks.length ||
				this.block.metaId
			);
		},
	},
	methods: {
		onAvatarError: function () {
			this.failedAvatarUrl = this.avatarUrl;
		},
		onSavedByAvatarError: function () {
			this.failedSavedByAvatarUrl = this.savedByAvatarUrl;
		},
	},
};
</script>

<style lang="scss" scoped>
.c-enrichment-details {
	&__row {
		display: grid;
		grid-template-columns: 130px 20px minmax(0, 1fr);
		align-items: start;
		column-gap: 8px;
		padding: 5px 0;
		font-family: var(--font-family);
		font-size: 14px;
		line-height: 20px;
		letter-spacing: 0;
	}

	&__label {
		color: var(--color-font-light);
		opacity: 0.8;
		font-weight: 500;
	}

	&__icon {
		width: 20px;
		height: 20px;
		color: var(--color-font-light);
		stroke: currentColor;
		opacity: 0.8;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	&__avatar {
		width: 20px;
		height: 20px;
		border-radius: 50%;
		object-fit: cover;
	}

	&__saved-by-icon {
		width: 20px;
		height: 20px;
		color: var(--color-bg-6);
	}

	&__saved-by-value {
		display: flex;
		flex-wrap: wrap;
		column-gap: 10px;
	}

	&__saved-by-name {
		color: var(--color-font);
	}

	&__saved-by-date {
		opacity: 0.8;
	}

	&__networks {
		grid-column: 2 / -1;
		display: flex;
		flex-wrap: wrap;
		gap: 8px 12px;
	}

	&__network {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		color: var(--color-font-light);
		font-weight: 500;

		img {
			width: 20px;
			height: 20px;
			object-fit: contain;
		}
	}

	&__link {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: var(--color-font-light);
		font-weight: 500;
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}

	&__emoji {
		display: inline-flex;
		width: 20px;
		height: 20px;
		align-items: center;
		justify-content: center;
		font-size: 18px;
		line-height: 1;
	}

	&__status-dot {
		width: 6px;
		height: 6px;
		justify-self: center;
		margin-top: 7px;
		color: var(--color-bg-6);

		&.active {
			color: var(--color-success);
		}

		&.pending {
			color: var(--color-warning);
		}
	}

	&__value {
		min-width: 0;
		color: var(--color-font-light);
		font-weight: 500;
		word-break: break-word;
	}
}

@media screen and (max-width: 400px) {
	.c-enrichment-details__row {
		//grid-template-columns: 100px 20px minmax(0, 1fr);
		//column-gap: 8px;
	}
}
</style>
